/**
 * @name Dynamic Yield Utilities
 *
 * @description
 * Shared utilities for setting the Dynamic Yield recommendation context.
 * Used by Home, Editorial, and PLP pages.
 */

interface DyContextOptions {
    /** The DY context type (e.g. 'HOMEPAGE', 'CATEGORY', 'OTHER') */
    type: string
    /** Optional data array (e.g. category IDs for PLP) */
    data?: string[]
    /** The locale ID (e.g. 'it-IT', 'en-US') — will be transformed to DY format ('it_IT') */
    localeId?: string
}

declare global {
    interface Window {
        DY?: {
            recommendationContext?: Record<string, unknown>
            API?: (action: string, data: Record<string, unknown>) => void
        }
    }
}

/**
 * Sets the Dynamic Yield recommendation context on `window.DY`.
 *
 * Handles:
 * - Initializing `window.DY` if absent
 * - Locale transformation (e.g. 'it-IT' → 'it_IT')
 * - Assigning `recommendationContext` with type, optional data, and lng
 *
 * @param options - The DY context configuration
 */
function setDyRecommendationContext({type, data, localeId}: DyContextOptions): void {
    if (typeof window === 'undefined') return

    const dyLocale = localeId ? localeId.replace('-', '_') : undefined

    window.DY = window.DY || {}
    window.DY.recommendationContext = {
        type,
        ...(data && data.length > 0 ? {data} : {}),
        ...(dyLocale ? {lng: dyLocale} : {}),
    }
}

export {setDyRecommendationContext}
export type {DyContextOptions}
